import { z } from 'zod';
import { nullableList } from '../zod-helpers';

const uuid = z.string().uuid();

export const translationStatusSchema = z.enum(['missing', 'drafted', 'reviewed', 'published']);
export type TranslationStatus = z.infer<typeof translationStatusSchema>;

export const translationSubjectKindSchema = z.enum([
  'post',
  'story',
  'event',
  'group',
  'announcement',
  'brand',
  'product',
  'pillar',
  'member_story',
  'chapter',
]);
export type TranslationSubjectKind = z.infer<typeof translationSubjectKindSchema>;

export const translationLocaleSchema = z.enum(['en', 'ar', 'eg-ar']);
export type TranslationLocale = z.infer<typeof translationLocaleSchema>;

export const translationTaskSchema = z.object({
  id: uuid,
  subject_kind: translationSubjectKindSchema,
  subject_id: uuid,
  field: z.string(),
  locale: translationLocaleSchema,
  status: translationStatusSchema,
  assigned_to: uuid.nullable().optional(),
  draft_en: z.string().nullable().optional(),
  draft_ar: z.string().nullable().optional(),
  draft_eg_ar: z.string().nullable().optional(),
  submitted_at: z.string().nullable().optional(),
  reviewed_at: z.string().nullable().optional(),
  published_at: z.string().nullable().optional(),
  updated_at: z.string().nullable().optional(),
});
export type TranslationTask = z.infer<typeof translationTaskSchema>;
export const translationTaskListSchema = nullableList(translationTaskSchema);

export const translationListFiltersSchema = z.object({
  status: translationStatusSchema.optional(),
  subject_kind: translationSubjectKindSchema.optional(),
  assigned_to: uuid.optional(),
  limit: z.number().int().min(1).max(100).optional(),
});
export type TranslationListFilters = z.infer<typeof translationListFiltersSchema>;

export const translationAssignRequestSchema = z.object({
  assigned_to: uuid,
});
export type TranslationAssignRequest = z.infer<typeof translationAssignRequestSchema>;

export const translationSubmitRequestSchema = z
  .object({
    draft_en: z.string().max(20_000).optional(),
    draft_ar: z.string().max(20_000).optional(),
    draft_eg_ar: z.string().max(20_000).optional(),
  })
  .refine(
    (v) => {
      const en = v.draft_en ?? '';
      const ar = v.draft_ar ?? '';
      const egAr = v.draft_eg_ar ?? '';
      return en.length > 0 || ar.length > 0 || egAr.length > 0;
    },
    {
      message: 'Provide at least one of draft_en, draft_ar, or draft_eg_ar',
      path: ['draft_en'],
    }
  );
export type TranslationSubmitRequest = z.infer<typeof translationSubmitRequestSchema>;

/** Server-side allow-list per docs/api/admin/translations.md. */
export const TRANSLATION_FIELDS_BY_SUBJECT: Readonly<
  Record<TranslationSubjectKind, readonly string[]>
> = {
  post: ['body'],
  story: ['title', 'excerpt'],
  event: ['title', 'summary', 'body'],
  group: ['name', 'desc'],
  announcement: ['title', 'body'],
  brand: ['name', 'bio'],
  product: ['name', 'description'],
  pillar: ['name', 'submessage'],
  member_story: ['title', 'body'],
  chapter: ['name', 'desc'],
};
